import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../common/components/base_form_controlled_modal";
import { FormDatePickerInput } from "../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DefaultFormMultiAutocomplete } from "../../../../common/components/default_form_multi_autocomplete";
import { DefaultFormSelectInput } from "../../../../common/components/default_form_select_input";
import { useCallback, useEffect, useMemo } from "react";
import { initialReportMapaConcretagem, ReportMapaConcretagemColumnsForm, ReportMapaConcretagemForm, ReportMapaConcretagemSchema } from "../schemas/Obras_informacoesgerais/report_mapa_concretagem";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormGroup } from "@mui/material";
import { TableOptions } from "../../../../common/components/table_options";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { MapaConcretagemFiltro } from "../../domain/models/mapa_concretagem_filtro";

import FormContainer from "../../../../common/components/form_container";
import DefaultFormInput from "../../../../common/components/default_form_input";
import PrimaryButton from "../../../../common/components/primary_button";

/**
 * ReportMapaConcretagemModal
 */

type Props = {
  open: boolean;
  loading: boolean;
  saving: boolean;
  filtros: MapaConcretagemFiltro | null,
  fornecedores: FornecedorDropDown[],
  onClose: (openState: boolean) => void;
  onSubmit: (form: ReportMapaConcretagemForm) => void;
  onSubmitColumns: (colunas: ReportMapaConcretagemColumnsForm) => void;
};

export const ReportMapaConcretagemModal = ({
  open,
  loading,
  saving,
  filtros,
  fornecedores,
  onClose,
  onSubmit,
  onSubmitColumns,
}: Props) => {
  const hookForm = useForm<ReportMapaConcretagemForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialReportMapaConcretagem,
    resolver: zodResolver(ReportMapaConcretagemSchema),
  });

  const [c] = hookForm.watch(['colunas']);

  const statusRompimentoOptions = useMemo(() => {
    return [
      { id: 2, label: 'Todos' },
      { id: 0, label: 'Rompimento em dia' },
      { id: 1, label: 'Rompimento pendente' },
    ];
  }, [
  ]);

  const enviadoLaboratorioOptions = useMemo(() => {
    return [
      { id: 2, label: 'Todos' },
      { id: 1, label: 'Sim' },
      { id: 0, label: 'Não' },
    ];
  }, [
  ]);

  const selectAll = useMemo(() => {
    if (!c) return false;
    return (
      c.dataConcretagem &&
      c.tipoDoConcretoSolicitado &&
      c.slumpSolicit &&
      c.ensaioDoConcreto &&
      c.resistMpa &&
      c.moduloResistenciaGPA &&
      c.ensaioModuloElasticidade &&
      c.fornecedor &&
      c.nroDoCarro &&
      c.nroNotaFiscal &&
      c.saidaUsina &&
      c.entObra &&
      c.inicioLanc &&
      c.finalLanc &&
      c.qtdeM3 &&
      c.slump &&
      c.localDeLancamento &&
      c.dataDeEnvioDaObra &&
      c.laboratorio &&
      c.nroCorpoDeProva &&
      c.nroLote &&
      c.nroLaudo &&
      c.instrucaoDeRompimento &&
      c.enviadoParaOLaboratorio &&
      c.dataDeEnvioParaOLaboratorio &&
      c.dozeHoras &&
      c.dataDozeHoras &&
      c.quatroDias &&
      c.dataQuatroDias &&
      c.seteDias &&
      c.dataSeteDias &&
      c.quatorzeDias &&
      c.dataQuatorzeDias &&
      c.vinteUmDias &&
      c.dataVinteUmDias &&
      c.vinteOitoDias &&
      c.dataVinteOitoDias &&
      c.cinquentaSeisDias &&
      c.dataCinquentaSeisDias &&
      c.sessentaTresDias &&
      c.dataSessentaTresDias &&
      c.elasticidade28 &&
      c.dataElasticidade28 &&
      c.outrasIdades1 &&
      c.dataOutrasIdades1 &&
      c.outrasIdades2 &&
      c.dataOutrasIdades2 &&
      c.outrasIdades3 &&
      c.dataOutrasIdades3 &&
      c.aprovado &&
      c.responsavelPelaAprovacao &&
      c.dataDaAprovacao &&
      c.observacao
    );
  }, [
    c,
  ]);

  useEffect(() => {
    if (open) {
      hookForm.reset({
        ...initialReportMapaConcretagem,
      });

      if (filtros && filtros.colunas && filtros.colunas.length > 0) {
        var colunasArr = filtros.colunas.split(',');
        hookForm.setValue('colunas', {
          dataConcretagem: colunasArr.includes('DATA_CONCRETAGEM'),
          tipoDoConcretoSolicitado: colunasArr.includes('TIPO_CONCRETO'),
          slumpSolicit: colunasArr.includes('SLUMP_SOLICIT'),
          ensaioDoConcreto: colunasArr.includes('ENSAIO_CONCRETO'),
          resistMpa: colunasArr.includes('RESIST_MPA'),
          moduloResistenciaGPA: colunasArr.includes('MODULO_RESISTENCIA_GPA'),
          ensaioModuloElasticidade: colunasArr.includes('ENSAIO_MODULO_ELASTICIDADE'),
          fornecedor: colunasArr.includes('DESC_FORNECEDOR'),
          nroDoCarro: colunasArr.includes('NRO_CARRO'),
          nroNotaFiscal: colunasArr.includes('NRO_NOTA_FISCAL'),
          saidaUsina: colunasArr.includes('SAIDA_USINA'),
          entObra: colunasArr.includes('DATA_ENTRADA'),
          inicioLanc: colunasArr.includes('DATA_INICIO'),
          finalLanc: colunasArr.includes('DATA_FINAL'),
          qtdeM3: colunasArr.includes('QTDE_M3'),
          slump: colunasArr.includes('SLUMP'),
          localDeLancamento: colunasArr.includes('LOCAL_LANCAMENTO'),
          dataDeEnvioDaObra: colunasArr.includes('DATA_ENVIO'),
          laboratorio: colunasArr.includes('LABORATORIO'),
          nroCorpoDeProva: colunasArr.includes('NUMERO_CORPO_PROVA'),
          nroLote: colunasArr.includes('NUMERO_LOTE'),
          nroLaudo: colunasArr.includes('NRO_LAUDO'),
          instrucaoDeRompimento: colunasArr.includes('INSTRUCAO_ROMPIMENTO'),
          enviadoParaOLaboratorio: colunasArr.includes('ENVIADO_LABORATORIO'),
          dataDeEnvioParaOLaboratorio: colunasArr.includes('DATA_ENVIADO_LABORATORIO'),
          dozeHoras: colunasArr.includes('RESISTENCIA_12HS'),
          dataDozeHoras: colunasArr.includes('DATA_12HS'),
          quatroDias: colunasArr.includes('RESISTENCIA_4'),
          dataQuatroDias: colunasArr.includes('DATA_4'),
          seteDias: colunasArr.includes('RESISTENCIA_7'),
          dataSeteDias: colunasArr.includes('DATA_7'),
          quatorzeDias: colunasArr.includes('RESISTENCIA_14'),
          dataQuatorzeDias: colunasArr.includes('DATA_14'),
          vinteUmDias: colunasArr.includes('RESISTENCIA_21'),
          dataVinteUmDias: colunasArr.includes('DATA_21'),
          vinteOitoDias: colunasArr.includes('RESISTENCIA_28'),
          dataVinteOitoDias: colunasArr.includes('DATA_28'),
          cinquentaSeisDias: colunasArr.includes('RESISTENCIA_56'),
          dataCinquentaSeisDias: colunasArr.includes('DATA_56'),
          sessentaTresDias: colunasArr.includes('RESISTENCIA_63'),
          dataSessentaTresDias: colunasArr.includes('DATA_63'),
          elasticidade28: colunasArr.includes('ELASTICIDADE_28'),
          dataElasticidade28: colunasArr.includes('DATA_ELASTICIDADE_28'),
          outrasIdades1: colunasArr.includes('TEMPO_RESULTADO1'),
          dataOutrasIdades1: colunasArr.includes('DATA_OUTRO1'),
          outrasIdades2: colunasArr.includes('TEMPO_RESULTADO2'),
          dataOutrasIdades2: colunasArr.includes('DATA_OUTRO2'),
          outrasIdades3: colunasArr.includes('TEMPO_RESULTADO3'),
          dataOutrasIdades3: colunasArr.includes('DATA_OUTRO3'),
          aprovado: colunasArr.includes('APROVADO'),
          responsavelPelaAprovacao: colunasArr.includes('RESPONSAVEL_APROVACAO'),
          dataDaAprovacao: colunasArr.includes('DATA_APROVACAO'),
          observacao: colunasArr.includes('OBSERVACAO'),
        });
      }
    }
  }, [
    open,
    filtros,
    hookForm,
  ]);

  const handleToggleSelectAll = useCallback((checked: boolean) => {
    hookForm.setValue('colunas', {
      dataConcretagem: checked,
      tipoDoConcretoSolicitado: checked,
      slumpSolicit: checked,
      ensaioDoConcreto: checked,
      resistMpa: checked,
      moduloResistenciaGPA: checked,
      ensaioModuloElasticidade: checked,
      fornecedor: checked,
      nroDoCarro: checked,
      nroNotaFiscal: checked,
      saidaUsina: checked,
      entObra: checked,
      inicioLanc: checked,
      finalLanc: checked,
      qtdeM3: checked,
      slump: checked,
      localDeLancamento: checked,
      dataDeEnvioDaObra: checked,
      laboratorio: checked,
      nroCorpoDeProva: checked,
      nroLote: checked,
      nroLaudo: checked,
      instrucaoDeRompimento: checked,
      enviadoParaOLaboratorio: checked,
      dataDeEnvioParaOLaboratorio: checked,
      dozeHoras: checked,
      dataDozeHoras: checked,
      quatroDias: checked,
      dataQuatroDias: checked,
      seteDias: checked,
      dataSeteDias: checked,
      quatorzeDias: checked,
      dataQuatorzeDias: checked,
      vinteUmDias: checked,
      dataVinteUmDias: checked,
      vinteOitoDias: checked,
      dataVinteOitoDias: checked,
      cinquentaSeisDias: checked,
      dataCinquentaSeisDias: checked,
      sessentaTresDias: checked,
      dataSessentaTresDias: checked,
      elasticidade28: checked,
      dataElasticidade28: checked,
      outrasIdades1: checked,
      dataOutrasIdades1: checked,
      outrasIdades2: checked,
      dataOutrasIdades2: checked,
      outrasIdades3: checked,
      dataOutrasIdades3: checked,
      aprovado: checked,
      responsavelPelaAprovacao: checked,
      dataDaAprovacao: checked,
      observacao: checked,
    });
  }, [
    hookForm,
  ]);

  const handleToggleOptionChange = useCallback((field: keyof ReportMapaConcretagemForm['colunas'], checked: boolean) => {
    const colunas = hookForm.getValues('colunas');
    colunas[field] = checked;
    hookForm.setValue('colunas', { ...colunas });
  }, [
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title={'Relatório Mapa de Concretagem'}
      width='70rem'
      confirmButtonLabel='Gerar relatório'
      confirmSavingButtonLabel='Gerando relatório...'
      savingLabel="Gerando"
      leftButtonSlot={<PrimaryButton onClick={() => onSubmitColumns(c)}>Salvar Colunas</PrimaryButton>}
      invertButtons={true}
      onClose={() => onClose(false)}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full grid grid-cols-10 grid-rows-1 gap-2 p-4">
        <div className="col-span-6">
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <div className="w-full flex gap-1">
                <FormDatePickerInput
                  name='dataInicio'
                  label='Data De'
                  reactHookForm={hookForm} />

                <FormDatePickerInput
                  name='dataFim'
                  label='Data Ate'
                  reactHookForm={hookForm} />

              </div>

              <DefaultFormMultiAutocomplete
                name='fornecedores'
                label='Fornecedor'
                options={fornecedores}
                getOptionKey={x => x.idFornecedor}
                getOptionLabel={x => x.nomeFantasia}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='localLancamento'
                label='Local do Lançamento'
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='resistencia'
                label='Resistência'
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='nroNotaFiscal'
                label='Nº da Nota Fiscal'
                reactHookForm={hookForm} />

              <DefaultFormSelectInput
                name='statusRompimento'
                label='Status do rompimento'
                showSelectionLabel={false}
                options={statusRompimentoOptions}
                reactHookForm={hookForm} />

              <DefaultFormSelectInput
                name='enviadoLaboratorio'
                label='Enviado para laboratório'
                showSelectionLabel={false}
                options={enviadoLaboratorioOptions}
                reactHookForm={hookForm} />
            </>
          </FormContainer>
        </div>

        <div className="col-start-7 col-span-4 px-8 py-8">
          <FormGroup>
            <TableOptions label="EXIBIR TODAS AS COLUNAS" checked={selectAll} onChange={c => handleToggleSelectAll(c)} />

            <div className="flex flex-col pl-4">
              <TableOptions checked={c?.dataConcretagem} label="DATA CONCRETAGEM" onChange={c => handleToggleOptionChange('dataConcretagem', c)} />
              <TableOptions checked={c?.tipoDoConcretoSolicitado} label="TIPO DO CONCRETO SOLICITADO" onChange={c => handleToggleOptionChange('tipoDoConcretoSolicitado', c)} />
              <TableOptions checked={c?.slumpSolicit} label="SLUMP SOLICIT." onChange={c => handleToggleOptionChange('slumpSolicit', c)} />
              <TableOptions checked={c?.ensaioDoConcreto} label="ENSAIO DO CONCRETO" onChange={c => handleToggleOptionChange('ensaioDoConcreto', c)} />
              <TableOptions checked={c?.resistMpa} label="RESIST. (MPA)" onChange={c => handleToggleOptionChange('resistMpa', c)} />
              <TableOptions checked={c?.moduloResistenciaGPA} label="MODULO RESISTENCIA GPA" onChange={c => handleToggleOptionChange('moduloResistenciaGPA', c)} />
              <TableOptions checked={c?.ensaioModuloElasticidade} label="ENSAIO MODULO ELASTICIDADE" onChange={c => handleToggleOptionChange('ensaioModuloElasticidade', c)} />
              <TableOptions checked={c?.fornecedor} label="FORNECEDOR" onChange={c => handleToggleOptionChange('fornecedor', c)} />
              <TableOptions checked={c?.nroDoCarro} label="Nº DO CARRO" onChange={c => handleToggleOptionChange('nroDoCarro', c)} />
              <TableOptions checked={c?.nroNotaFiscal} label="Nº NOTA FISCAL" onChange={c => handleToggleOptionChange('nroNotaFiscal', c)} />
              <TableOptions checked={c?.saidaUsina} label="SAÍDA USINA" onChange={c => handleToggleOptionChange('saidaUsina', c)} />
              <TableOptions checked={c?.entObra} label="ENT. OBRA" onChange={c => handleToggleOptionChange('entObra', c)} />
              <TableOptions checked={c?.inicioLanc} label="INÍCIO LANÇ." onChange={c => handleToggleOptionChange('inicioLanc', c)} />
              <TableOptions checked={c?.finalLanc} label="FINAL LANÇ." onChange={c => handleToggleOptionChange('finalLanc', c)} />
              <TableOptions checked={c?.qtdeM3} label="QTDE. (M3)" onChange={c => handleToggleOptionChange('qtdeM3', c)} />
              <TableOptions checked={c?.slump} label="SLUMP" onChange={c => handleToggleOptionChange('slump', c)} />
              <TableOptions checked={c?.localDeLancamento} label="LOCAL DE LANÇAMENTO" onChange={c => handleToggleOptionChange('localDeLancamento', c)} />
              <TableOptions checked={c?.dataDeEnvioDaObra} label="DATA DE ENVIO DA OBRA" onChange={c => handleToggleOptionChange('dataDeEnvioDaObra', c)} />
              <TableOptions checked={c?.laboratorio} label="LABORATÓRIO" onChange={c => handleToggleOptionChange('laboratorio', c)} />
              <TableOptions checked={c?.nroCorpoDeProva} label="N° CORPO DE PROVA" onChange={c => handleToggleOptionChange('nroCorpoDeProva', c)} />
              <TableOptions checked={c?.nroLote} label="N° LOTE" onChange={c => handleToggleOptionChange('nroLote', c)} />
              <TableOptions checked={c?.nroLaudo} label="N° LAUDO" onChange={c => handleToggleOptionChange('nroLaudo', c)} />
              <TableOptions checked={c?.instrucaoDeRompimento} label="INSTRUÇÃO DE ROMPIMENTO" onChange={c => handleToggleOptionChange('instrucaoDeRompimento', c)} />
              <TableOptions checked={c?.enviadoParaOLaboratorio} label="ENVIADO PARA O LABORATÓRIO" onChange={c => handleToggleOptionChange('enviadoParaOLaboratorio', c)} />
              <TableOptions checked={c?.dataDeEnvioParaOLaboratorio} label="DATA DE ENVIO PARA O LABORATÓRIO" onChange={c => handleToggleOptionChange('dataDeEnvioParaOLaboratorio', c)} />
              <TableOptions checked={c?.dozeHoras} label="12 HORAS" onChange={c => handleToggleOptionChange('dozeHoras', c)} />
              <TableOptions checked={c?.dataDozeHoras} label="DATA 12 HORAS" onChange={c => handleToggleOptionChange('dataDozeHoras', c)} />
              <TableOptions checked={c?.quatroDias} label="3 DIAS" onChange={c => handleToggleOptionChange('quatroDias', c)} />
              <TableOptions checked={c?.dataQuatroDias} label="DATA 3 DIAS" onChange={c => handleToggleOptionChange('dataQuatroDias', c)} />
              <TableOptions checked={c?.seteDias} label="7 DIAS" onChange={c => handleToggleOptionChange('seteDias', c)} />
              <TableOptions checked={c?.dataSeteDias} label="DATA 7 DIAS" onChange={c => handleToggleOptionChange('dataSeteDias', c)} />
              <TableOptions checked={c?.quatorzeDias} label="14 DIAS" onChange={c => handleToggleOptionChange('quatorzeDias', c)} />
              <TableOptions checked={c?.dataQuatorzeDias} label="DATA 14 DIAS" onChange={c => handleToggleOptionChange('dataQuatorzeDias', c)} />
              <TableOptions checked={c?.vinteUmDias} label="21 DIAS" onChange={c => handleToggleOptionChange('vinteUmDias', c)} />
              <TableOptions checked={c?.dataVinteUmDias} label="DATA 21 DIAS" onChange={c => handleToggleOptionChange('dataVinteUmDias', c)} />
              <TableOptions checked={c?.vinteOitoDias} label="28 DIAS" onChange={c => handleToggleOptionChange('vinteOitoDias', c)} />
              <TableOptions checked={c?.dataVinteOitoDias} label="DATA 28 DIAS" onChange={c => handleToggleOptionChange('dataVinteOitoDias', c)} />
              <TableOptions checked={c?.cinquentaSeisDias} label="56 DIAS" onChange={c => handleToggleOptionChange('cinquentaSeisDias', c)} />
              <TableOptions checked={c?.dataCinquentaSeisDias} label="DATA 56 DIAS" onChange={c => handleToggleOptionChange('dataCinquentaSeisDias', c)} />
              <TableOptions checked={c?.sessentaTresDias} label="63 DIAS" onChange={c => handleToggleOptionChange('sessentaTresDias', c)} />
              <TableOptions checked={c?.dataSessentaTresDias} label="DATA 63 DIAS" onChange={c => handleToggleOptionChange('dataSessentaTresDias', c)} />
              <TableOptions checked={c?.elasticidade28} label="ELASTICIDADE 28 DIAS" onChange={c => handleToggleOptionChange('elasticidade28', c)} />
              <TableOptions checked={c?.dataElasticidade28} label="DATA ELASTICIDADE 28 DIAS" onChange={c => handleToggleOptionChange('dataElasticidade28', c)} />
              <TableOptions checked={c?.outrasIdades1} label="OUTRAS IDADES 1" onChange={c => handleToggleOptionChange('outrasIdades1', c)} />
              <TableOptions checked={c?.dataOutrasIdades1} label="DATA OUTRAS IDADES 1" onChange={c => handleToggleOptionChange('dataOutrasIdades1', c)} />
              <TableOptions checked={c?.outrasIdades2} label="OUTRAS IDADES 2" onChange={c => handleToggleOptionChange('outrasIdades2', c)} />
              <TableOptions checked={c?.dataOutrasIdades2} label="DATA OUTRAS IDADES 2" onChange={c => handleToggleOptionChange('dataOutrasIdades2', c)} />
              <TableOptions checked={c?.outrasIdades3} label="OUTRAS IDADES 3" onChange={c => handleToggleOptionChange('outrasIdades3', c)} />
              <TableOptions checked={c?.dataOutrasIdades3} label="DATA OUTRAS IDADES 3" onChange={c => handleToggleOptionChange('dataOutrasIdades3', c)} />
              <TableOptions checked={c?.aprovado} label="APROVADO?" onChange={c => handleToggleOptionChange('aprovado', c)} />
              <TableOptions checked={c?.responsavelPelaAprovacao} label="RESPONSÁVEL PELA APROVAÇÃO" onChange={c => handleToggleOptionChange('responsavelPelaAprovacao', c)} />
              <TableOptions checked={c?.dataDaAprovacao} label="DATA DA APROVACAO" onChange={c => handleToggleOptionChange('dataDaAprovacao', c)} />
              <TableOptions checked={c?.observacao} label="OBSERVACAO" onChange={c => handleToggleOptionChange('observacao', c)} />
            </div>
          </FormGroup>
        </div>
      </div>
    </BaseFormControlledModal>
  );
}